import { camelCase } from 'lodash-es';
import { TFunction, Trans, useTranslation } from 'next-i18next/pages';

import { JobPosition } from '@pflegia/types/api/job-position/job-position';
import { ConnectionInfo } from '@pflegia/types/common/match/dialog';
import { Badge } from '@pflegia/ui/shared/badge/Badge.component';
import EmojiText from '@pflegia/ui/shared/emoji-text';
import { formatSalaryRange } from '@pflegia/util/common/format-salary-range';

import OffersDialogueButton from '@shared/components/dialogue/OffersDialogueButton';

export interface WhyThisJobSectionProps {
  jobPosition: Pick<
    JobPosition,
    | 'id'
    | 'jobTitles'
    | 'title'
    | 'slug'
    | 'salaryLow'
    | 'salaryHigh'
    | 'showSalary'
    | 'vacationDays'
    | 'geoLocation'
    | 'employer'
    | 'canStartNow'
    | 'employmentTypes'
    | 'partTimeHours'
    | 'fullTimeHours'
  >;
  openDialog: (connectionInfo: ConnectionInfo) => void;
}

const PART_TIME = 'part_time';

const formatEmploymentTypes = (t: TFunction, employmentTypes?: string[] | null, partTimeHours?: number | null): string | null => {
  if (!employmentTypes?.length) return null;
  const labels = employmentTypes.map(type => {
    const baseLabel = t(`candidateFields:workingHours.types.${camelCase(type)}`);
    if (type === PART_TIME && partTimeHours && partTimeHours > 0) {
      return `${baseLabel} (${partTimeHours} ${t('candidateFields:hours')})`;
    }
    return baseLabel;
  });
  return labels.join(', ');
};

const buildLocationLabel = (t: TFunction, cityName: string | null, canStartNow?: boolean | null): string | null => {
  if (!cityName) return null;
  if (!canStartNow) return cityName;
  return t('whyThisJob.chips.locationWithStart', {
    city: cityName,
    startOfWork: t('whyThisJob.chips.startNow'),
  });
};

const WhyThisJobSection: React.FC<WhyThisJobSectionProps> = ({ jobPosition, openDialog }) => {
  const { t } = useTranslation(['jobDetails', 'candidateFields']);
  const salaryRange =
    jobPosition.showSalary && jobPosition.employer?.showSalary ? formatSalaryRange(jobPosition.salaryLow, jobPosition.salaryHigh) : null;
  const salary = salaryRange ? t('whyThisJob.chips.salary', { range: salaryRange }) : null;
  const vacation = jobPosition.vacationDays ? t('whyThisJob.chips.holidays', { days: jobPosition.vacationDays }) : null;
  const employment = formatEmploymentTypes(t, jobPosition.employmentTypes, jobPosition.partTimeHours);
  const location = buildLocationLabel(t, jobPosition.geoLocation?.city ?? null, jobPosition.canStartNow);

  return (
    <div className='rounded-2xl bg-blue-100 p-6 md:p-8'>
      <h2 className='text-default mb-4 text-xl font-bold md:text-2xl'>
        <Trans
          t={t}
          i18nKey='whyThisJob.title'
          components={{ span: <span className='font-handwriting text-primary text-2xl font-normal md:text-3xl' /> }}
        />
      </h2>

      <div className='mb-6 flex w-full flex-wrap justify-start gap-2 text-xs md:text-sm'>
        {salary ? (
          <Badge className='no-wrap bg-white py-1 pr-3 pl-2 font-normal'>
            <EmojiText emoji='💶' emojiAsText>
              {salary}
            </EmojiText>
          </Badge>
        ) : null}
        {vacation ? (
          <Badge className='no-wrap bg-white py-1 pr-3 pl-2 font-normal'>
            <EmojiText emoji='🌴' emojiAsText>
              {vacation}
            </EmojiText>
          </Badge>
        ) : null}
        {employment ? (
          <Badge className='no-wrap bg-white py-1 pr-3 pl-2 font-normal'>
            <EmojiText emoji='🕣' emojiAsText>
              {employment}
            </EmojiText>
          </Badge>
        ) : null}
        {location ? (
          <Badge className='no-wrap bg-white py-1 pr-3 pl-2 font-normal'>
            <EmojiText emoji='📍' emojiAsText>
              {location}
            </EmojiText>
          </Badge>
        ) : null}
      </div>

      <OffersDialogueButton
        className='w-full md:w-auto'
        jobPositionId={jobPosition.id}
        jobTitle={jobPosition.title}
        slug={jobPosition.slug}
        employerName={jobPosition.employer?.name}
        label={t('whyThisJob.cta')}
        openDialog={openDialog}
      />

      <p className='text-default mt-3 text-sm'>{t('whyThisJob.subline')}</p>
    </div>
  );
};

export default WhyThisJobSection;
